import { DataGrid, GridCellEditStopReasons } from '@mui/x-data-grid';
import { itIT } from '@mui/x-data-grid/locales';

import Empty from '../Empty';

import s from './DataTable.module.scss';

const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 20];

function NoRowsOverlay({ message }) {
  return (
    <div className={s.noRows}>
      <Empty title={message ?? 'Nessun elemento da mostrare'} />
    </div>
  );
}

/**
 * DataTable — adapter di `@mui/x-data-grid` con config standard del DESIGN.md.
 *
 * Default applicati:
 * - locale italiano (`itIT`),
 * - `density="compact"`,
 * - `pageSizeOptions=[5, 10, 20]`, default 10 righe per pagina,
 * - hover row in `#f0f9ff` (token-driven via SCSS),
 * - cell/header border `1px solid #ccc`,
 * - empty state via `<Empty>` quando `rows` è vuoto.
 *
 * Tutte le props di `<DataGrid>` sono passate attraverso. `columns` segue
 * l'API MUI (`field`, `headerName`, `renderCell`, `valueGetter`, ecc.).
 *
 * @param {object} props
 * @param {Array}  props.rows
 * @param {Array}  props.columns
 * @param {boolean} [props.loading]
 * @param {string}  [props.noRowsLabel] — testo dell'empty state
 * @param {Array<number>} [props.pageSizeOptions=[5,10,20]]
 * @param {number} [props.defaultPageSize=10]
 * @param {boolean} [props.autoHeight=true]
 * @param {string}  [props.className]
 */
// AppSelect renders its menu in a portal on <body>: the mouseup on an option
// lands outside the editing cell, so the grid would stop edit mode with the
// old value before react-select's onChange (fired on click) can commit the
// new one. Keep the editor open in that case — the edit cell itself calls
// stopCellEditMode after committing.
const isAppSelectMenuClick = (event) =>
  event?.target instanceof Element &&
  Boolean(event.target.closest('.app-select__menu-portal, .app-select__menu'));

export default function DataTable({
  rows = [],
  columns = [],
  loading = false,
  noRowsLabel,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  defaultPageSize = 10,
  autoHeight = true,
  className,
  initialState,
  onCellEditStop,
  ...rest
}) {
  const mergedInitialState = {
    pagination: {
      paginationModel: { pageSize: defaultPageSize, page: 0 },
    },
    ...(initialState ?? {}),
  };

  const handleCellEditStop = (params, event, details) => {
    if (params.reason === GridCellEditStopReasons.cellFocusOut && isAppSelectMenuClick(event)) {
      event.defaultMuiPrevented = true;
      return;
    }
    onCellEditStop?.(params, event, details);
  };

  return (
    <div className={[s.wrapper, className].filter(Boolean).join(' ')}>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        density="compact"
        autoHeight={autoHeight}
        pageSizeOptions={pageSizeOptions}
        initialState={mergedInitialState}
        localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
        slots={{ noRowsOverlay: () => <NoRowsOverlay message={noRowsLabel} /> }}
        disableRowSelectionOnClick
        onCellEditStop={handleCellEditStop}
        {...rest}
      />
    </div>
  );
}
